import {
  Billing,
  BillingTransaction,
  objectIdSchema,
  paginationWithSearchSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/** GET /billing-transaction — single BTx by id. */
export const billingTransactionGetOneAPISchema = z.object({
  _id: objectIdSchema,
});
export type BillingTransactionGetOneAPI = API<
  z.infer<typeof billingTransactionGetOneAPISchema>,
  BillingTransaction
>;

/** GET /billing-transaction/many — BTx list, scoped by billing or org. */
export const billingTransactionGetManyAPISchema = z
  .object({
    billingId: objectIdSchema.optional(),
    orgId: objectIdSchema.optional(),
    status: z.enum(['success', 'failed', 'waiting_for_approval']).optional(),
    type: z.enum(['charge', 'refund', 'chargeback']).optional(),
  })
  .extend(paginationWithSearchSchema.shape);
export type BillingTransactionGetManyAPI = API<
  z.infer<typeof billingTransactionGetManyAPISchema>,
  BillingTransaction[]
>;

/** POST /billing-transaction/admin/refund — admin issues a (partial) refund. */
export const billingTransactionAdminRefundAPISchema = z.object({
  billingId: objectIdSchema,
  amount: z.coerce.number().min(0.01),
  reason: z.string().max(500).optional(),
});
export type BillingTransactionAdminRefundAPI = API<
  z.infer<typeof billingTransactionAdminRefundAPISchema>,
  BillingTransaction
>;

/**
 * GET /billing-transaction/by-gateway-payment-id — resolve a Moyasar
 * payment id back to the BTx + parent Billing it belongs to.
 *
 * Used exclusively by the payment-confirmation return page: when
 * Moyasar redirects the user back to our `success_url` /
 * `callback_url`, it appends `?id=<moyasarPaymentId>&status=…`. We
 * read `id` from the URL and call this to find out which billing
 * we're showing the receipt for (and to poll its terminal status).
 *
 * Returns `transaction: null` when the webhook hasn't landed yet —
 * BTx rows are created only on settled webhook delivery, so the
 * return page polls until it materialises (with a "verifying…" UI in
 * the meantime). The endpoint is scoped to the caller's org
 * server-side, so this cannot be used to peek at another org's
 * payment.
 */
export const billingTransactionByGatewayPaymentIdAPISchema = z.object({
  id: z.string().min(1),
});
export type BillingTransactionByGatewayPaymentIdAPI = API<
  z.infer<typeof billingTransactionByGatewayPaymentIdAPISchema>,
  { transaction: BillingTransaction; billing: Billing } | { transaction: null }
>;
